import React, { useState, useContext, useRef, useEffect } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import UserContextProvider from 'interface/interfaces';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as eventServices from 'service/eventServices';
import { useIsLoggedIn } from 'hooks/useIsLoggedIn';

const formatEventDate = (dateVal: any): string => {
  if (!dateVal) return '';
  try {
    let d: Date;
    if (typeof dateVal === 'object' && dateVal !== null && '_seconds' in dateVal) {
      d = new Date(dateVal._seconds * 1000 + (dateVal._nanoseconds || 0) / 1000000);
    } else if (typeof dateVal === 'number') {
      d = new Date(dateVal);
    } else {
      d = new Date(dateVal);
    }
    if (isNaN(d.getTime())) return String(dateVal);

    return d.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(dateVal);
  }
};

const TicketFilterBar = () => {
  const context = useContext(UserContextProvider);
  const navigate = useNavigate();
  const isLoggedIn = useIsLoggedIn();

  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
  const [selectedEventoObj, setSelectedEventoObj] = useState<any>(null);
  const [selectedFecha, setSelectedFecha] = useState<string>('');
  const selectedCantidad = 1;

  const [openDropdown, setOpenDropdown] = useState<'evento' | 'fecha' | 'cantidad' | null>(null);

  const filterRef = useRef<HTMLDivElement>(null);

  const countryCode = context?.config?.countryCode || 'RAF';
  const storeKey = context?.config?.storeKey || '';

  // Cargar eventos del backend si no están disponibles en el contexto
  useEffect(() => {
    if (context?.events && context.events.length > 0) return;

    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const res: any = await eventServices.getEvents(countryCode, storeKey);
        if (Array.isArray(res) && res.length > 0) {
          context?.asignateEventsArray(res);
        }
      } catch (err) {
        console.error('Error al cargar eventos en TicketFilterBar:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, [context, countryCode, storeKey]);

  // Cierra el menú desplegable si se da click afuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleComprar = () => {
    if (!selectedEventoObj) {
      toast.info('Por favor selecciona un evento');
      return;
    }

    const eventKey = selectedEventoObj.$key || selectedEventoObj.id;
    const eventData = {
      id: eventKey,
      name: selectedEventoObj.name || selectedEventoObj.title || 'Evento',
      description: selectedEventoObj.shortDescription || selectedEventoObj.description || '',
      image: selectedEventoObj.image?.downloadURL || selectedEventoObj.image || '',
      location: selectedEventoObj.location || '',
      date: formatEventDate(selectedEventoObj.start_date),
      time: selectedEventoObj.start_date
        ? (() => {
          try {
            const d = selectedEventoObj.start_date._seconds
              ? new Date(selectedEventoObj.start_date._seconds * 1000)
              : new Date(selectedEventoObj.start_date);
            return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
          } catch {
            return '';
          }
        })()
        : '',
      ticket: selectedEventoObj.tickets,
      ticketsBanner: selectedEventoObj.ticketsBanner?.downloadURL || selectedEventoObj.ticketsBanner || '',
      activeZoneSelector: selectedEventoObj.activeZoneSelector,
      templateChart: selectedEventoObj.chartioTemplateEvent,
      popUpMobile: selectedEventoObj.pop_up?.mobile,
      popUpWeb: selectedEventoObj.pop_up?.web,
    };

    context.asignateEventData(eventData);
    localStorage.setItem('eventData', JSON.stringify(eventData));

    const nextPath = eventKey === 'Ui31XrZoEKZnYKoZbrwd' 
      ? `/salazon-info/${eventKey}` 
      : `/event/eventDetail/${eventKey}`;

    if (!isLoggedIn) {
      toast.info('Inicia sesión para continuar con tu compra');
      navigate('/login', { state: { next: nextPath } });
    } else {
      toast.success(`Redirigiendo a la compra de ${selectedCantidad} ticket(s) para ${eventData.name}...`);
      navigate(nextPath);
    }
  };

  const toggleDropdown = (menu: 'evento' | 'fecha' | 'cantidad') => {
    setOpenDropdown((prev) => (prev === menu ? null : menu));
  };

  const eventsList = (context?.events && context.events.length > 0) ? context.events : [];

  // Fechas dinámicas basadas en los eventos disponibles
  const availableDates = Array.from(
    new Set(
      eventsList
        .map((evt: any) => (evt.start_date ? formatEventDate(evt.start_date) : null))
        .filter(Boolean)
    )
  ) as string[];

  // const cantidadesList = [1, 2, 3, 4, 5, 6, 10];

  const selectedEventName = selectedEventoObj
    ? selectedEventoObj.name || selectedEventoObj.title || 'Evento'
    : '';

  return (
    <div
      ref={filterRef}
      className="hidden lg:flex items-center justify-between gap-4 lg:gap-6 bg-[#121316]/85 backdrop-blur-xl border border-white/15 rounded-full px-6 py-2.5 shadow-[0_10px_35px_rgba(0,0,0,0.8)] mx-auto w-full max-w-4xl xl:max-w-5xl transition-all duration-300 select-none my-3 relative z-30"
    >
      {/* Etiqueta Izquierda */}
      <span className="text-gray-300 italic text-xs lg:text-sm font-light whitespace-nowrap pl-2">
        Compra tus tickets en línea
      </span>

      {/* Contenedor de Dropdowns */}
      <div className="flex items-center gap-6 xl:gap-8">
        {/* Dropdown Evento */}
        <div className="relative">
          <button
            type="button"
            onClick={() => toggleDropdown('evento')}
            className="flex items-center gap-1.5 text-white font-medium text-xs lg:text-sm hover:text-[#E44036] transition-colors cursor-pointer whitespace-nowrap py-1"
          >
            <span>
              {selectedEventName
                ? selectedEventName.length > 18
                  ? `${selectedEventName.substring(0, 18)}...`
                  : selectedEventName
                : 'Seleccionar Evento'}
            </span>
            <KeyboardArrowDownIcon
              className={`!text-lg transition-transform duration-200 ${openDropdown === 'evento' ? 'rotate-180 text-[#E44036]' : 'text-gray-300'
                }`}
            />
          </button>

          {openDropdown === 'evento' && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-[#1a1b20] border border-gray-700 rounded-xl shadow-2xl py-2 z-50 animate-fadeIn max-h-60 overflow-y-auto">
              {selectedEventoObj && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedEventoObj(null);
                    setSelectedFecha('');
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-4 py-1.5 text-[11px] text-red-400 hover:bg-black/50 border-b border-gray-700/60 transition-colors"
                >
                  ✕ Todos los eventos
                </button>
              )}

              {loadingEvents && eventsList.length === 0 ? (
                <div className="px-4 py-3 text-xs text-gray-400 italic">Cargando eventos...</div>
              ) : eventsList.length === 0 ? (
                <div className="px-4 py-3 text-xs text-gray-400 italic">No hay eventos disponibles</div>
              ) : (
                eventsList.map((evt: any, idx: number) => {
                  const name = evt.name || evt.title || `Evento ${idx + 1}`;
                  const evtKey = evt.$key || evt.id || idx;
                  const isSelected =
                    selectedEventoObj && (selectedEventoObj.$key || selectedEventoObj.id) === evtKey;

                  return (
                    <button
                      key={evtKey}
                      type="button"
                      onClick={() => {
                        setSelectedEventoObj(evt);
                        if (evt.start_date) {
                          setSelectedFecha(formatEventDate(evt.start_date));
                        }
                        setOpenDropdown(null);
                      }}
                      className={`w-full text-left px-4 py-2 text-xs transition-colors truncate flex items-center justify-between gap-2 ${isSelected
                        ? 'bg-[#E44036]/20 text-[#E44036] font-semibold'
                        : 'text-gray-200 hover:bg-black/50 hover:text-[#E44036]'
                        }`}
                    >
                      <span className="truncate">{name}</span>
                      {evt.start_date && (
                        <span className="text-[10px] text-gray-400 font-normal shrink-0">
                          {formatEventDate(evt.start_date)}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Dropdown Fecha */}
        <div className="relative">
          <button
            type="button"
            onClick={() => toggleDropdown('fecha')}
            className="flex items-center gap-1.5 text-white font-medium text-xs lg:text-sm hover:text-[#E44036] transition-colors cursor-pointer whitespace-nowrap py-1"
          >
            <span>{selectedFecha || 'Fecha'}</span>
            <KeyboardArrowDownIcon
              className={`!text-lg transition-transform duration-200 ${openDropdown === 'fecha' ? 'rotate-180 text-[#E44036]' : 'text-gray-300'
                }`}
            />
          </button>

          {openDropdown === 'fecha' && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-[#1a1b20] border border-gray-700 rounded-xl shadow-2xl py-2 z-50 animate-fadeIn max-h-60 overflow-y-auto">
              {selectedFecha && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFecha('');
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-4 py-1.5 text-[11px] text-red-400 hover:bg-black/50 border-b border-gray-700/60 transition-colors"
                >
                  ✕ Cualquier fecha
                </button>
              )}

              {availableDates.length > 0 ? (
                availableDates.map((f, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setSelectedFecha(f);
                      // Si hay un único evento con esa fecha, seleccionarlo automáticamente
                      const matching = eventsList.filter(
                        (e: any) => formatEventDate(e.start_date) === f
                      );
                      if (matching.length === 1) {
                        setSelectedEventoObj(matching[0]);
                      }
                      setOpenDropdown(null);
                    }}
                    className={`w-full text-left px-4 py-2 text-xs transition-colors ${selectedFecha === f
                      ? 'bg-[#E44036]/20 text-[#E44036] font-semibold'
                      : 'text-gray-200 hover:bg-black/50 hover:text-[#E44036]'
                      }`}
                  >
                    {f}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-xs text-gray-400 italic">
                  {selectedEventoObj ? 'Sin fecha especificada' : 'No hay fechas disponibles'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dropdown Cantidad */}
        {/* <div className="relative">
          <button
            type="button"
            onClick={() => toggleDropdown('cantidad')}
            className="flex items-center gap-1.5 text-white font-medium text-xs lg:text-sm hover:text-[#E44036] transition-colors cursor-pointer whitespace-nowrap py-1"
          >
            <span>{selectedCantidad ? `Cantidad: ${selectedCantidad}` : 'Cantidad'}</span>
            <KeyboardArrowDownIcon
              className={`!text-lg transition-transform duration-200 ${openDropdown === 'cantidad' ? 'rotate-180 text-[#E44036]' : 'text-gray-300'
                }`}
            />
          </button>

          {openDropdown === 'cantidad' && (
            <div className="absolute top-full left-0 mt-2 w-36 bg-[#1a1b20] border border-gray-700 rounded-xl shadow-2xl py-2 z-50 animate-fadeIn">
              {cantidadesList.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setSelectedCantidad(c);
                    setOpenDropdown(null);
                  }}
                  className={`w-full text-left px-4 py-2 text-xs transition-colors ${selectedCantidad === c
                      ? 'bg-[#E44036]/20 text-[#E44036] font-semibold'
                      : 'text-gray-200 hover:bg-black/50 hover:text-[#E44036]'
                    }`}
                >
                  {c} {c === 1 ? 'ticket' : 'tickets'}
                </button>
              ))}
            </div>
          )}
        </div> */}
      </div>

      {/* Botón COMPRAR */}
      <button
        type="button"
        onClick={handleComprar}
        className="bg-[#E44036] hover:bg-[#c93229] active:scale-95 text-white font-extrabold uppercase text-xs tracking-wider px-5 py-2 rounded-full flex items-center gap-2 shadow-lg shadow-red-900/30 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
      >
        <ShoppingCartOutlinedIcon className="!text-lg" />
        <span>COMPRAR</span>
      </button>
    </div>
  );
};

export default TicketFilterBar;

